/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable indent */
import { Grid, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { AssignmentInd } from '@material-ui/icons';
import InputIcon from '@material-ui/icons/Input';
import Button from 'components/Button';
import ButtonExport from 'components/ButtonExport';
import PageTitle from 'components/PageTitle';
import SearchFilterBar from 'components/SearchFilterBar';
import TableItems, {
  MenuActionItem,
  MenuActions,
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import UserSession from 'containers/Users/UsersList/Sessions';
import { Query, useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import {
  activeUserInClientRequest,
  disableUserInClientRequest,
  editClientUserRequest,
  exportUserSummaryRequest,
  removeClientUserRequest,
} from 'modules/clients/actions';
import { FormUser as FormUserInterface } from 'modules/clients/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  getAllUsersRequest,
  removeUserFromAllClientsRequest,
} from 'modules/users/actions';
import {
  GetAllUsersFields,
  ListAllUsersDataProps,
} from 'modules/users/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { getCompanyName } from 'utils/tsHelpers';
import { filterOptions } from './filterOptions';
import FormUser from './FormUser';
import styles from './styles';
import { headers } from './tableHeaders';

const useStyles = makeStyles(styles);

interface Props {
  allUsersList: ListAllUsersDataProps;
  isLoading: boolean;
  permissions: PermissionsData | undefined;
  role?: RolesEnum;
  activeUserInClientRequest: ({ userUuid, id, query, isListAllUsers }) => void;
  disableUserInClientRequest: ({ userUuid, id, query, isListAllUsers }) => void;
  editClientUserRequest: (props: {
    clientUuid: string;
    userUuid: string;
    values: any;
    isListAllUsers: boolean;
    query?: Query;
  }) => void;
  exportUserSummaryRequest: (query) => void;
  removeUserFromAllClientsRequest: ({ userUuid, getUsersFn }) => void;
  getAllUsersRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  removeClientUserRequest: (props: {
    clientUuid: string;
    userUuid: string;
    isListAllUsers: boolean;
    query?: Query;
  }) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const ListAllUsers = ({
  allUsersList,
  isLoading,
  permissions,
  role,
  activeUserInClientRequest,
  disableUserInClientRequest,
  editClientUserRequest,
  exportUserSummaryRequest,
  removeUserFromAllClientsRequest,
  getAllUsersRequest,
  modalClose,
  modalOpen,
  removeClientUserRequest,
  selectCompany,
  selectPartner,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    itemOrder,
    query,
    searchValue,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setSearchValue,
  } = useTableFilters();

  const [refresh, setRefresh] = React.useState(false);
  const [showTable, setShowTable] = React.useState(false);

  const getUsers = React.useCallback(
    (refresh = false) => {
      if (!showTable) return;

      getAllUsersRequest({
        ...query,
      });
      setRefresh(refresh);
    },
    [getAllUsersRequest, query, showTable]
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (allUsersList.results.some(user => user.is_removing)) {
        getUsers(true);
      } else {
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsersList]);

  React.useEffect(() => {
    getUsers();
  }, [getUsers]);

  const mapUserSubmit = (values: FormUserInterface) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { username, ...props } = values;
    return props;
  };

  const handleClick = (item: GetAllUsersFields, toUrl: string) => {
    selectCompany({
      id: item?.company?.company_uuid,
      name: getCompanyName(item?.company),
      shortName: getCompanyName(item?.company),
    });
    selectPartner({
      name: item?.partner?.partner_name,
      shortName: item?.partner?.partner_name,
      id: item?.partner?.partner_uuid,
    });
    history.push(toUrl);
  };

  const getHeadersPDF = () => [
    headers
      .filter(header => header.key !== '')
      .map(header => i18n.t(header.i18n)),
  ];

  const getUsersListPDF: Array<[
    string,
    string,
    string,
    string,
    string,
    boolean,
    string,
    boolean,
    string
  ]> = allUsersList?.results.map(item => [
    `${item?.first_name} ${item?.last_name}`,
    item?.email,
    item?.client.client_name,
    item?.environment.environment_name,
    item?.partner.partner_name,
    item?.salt_configured,
    item?.active,
    item?.mfa_configured,
    String(formatDateValue(item?.last_access)),
  ]);

  const getCSV = () => {
    exportUserSummaryRequest({
      query,
    });
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('USERS_PAGE.LIST_ALL_USERS_KEY')}
          subtitle={i18n.t('USERS_PAGE.LIST_ALL_USERS_SUBTITLE_KEY')}
        />

        <Grid className={classes.actionsContainer}>
          {(permissions?.users_recovery || role === RolesEnum.superAdmin) && (
            <Button
              onClick={() =>
                history.push(
                  role === RolesEnum.superAdmin
                    ? '/all-users/management'
                    : '/all-users/recovery'
                )
              }
              className={classes.usersManagementButton}
              endIcon={<AssignmentInd />}
            >
              {i18n.t('USERS_PAGE.MANAGE_USERS_KEY')}
            </Button>
          )}

          {!!permissions && permissions?.can_export_user_summary_list && (
            <ButtonExport
              context={i18n.t('USERS_KEY')}
              getHeadersPDF={getHeadersPDF()}
              getBodyListPDF={getUsersListPDF}
              csvAction={getCSV}
              filters={query.filters}
              filterOptions={filterOptions}
            />
          )}
        </Grid>
      </Grid>
      <Grid container className={classes.searchContainer}>
        <SearchFilterBar
          filterOptions={filterOptions}
          isLoading={isLoading}
          searchValue={searchValue}
          query={query}
          onApplyFilters={() => setShowTable(true)}
          setSearchValue={setSearchValue}
          setQuery={setQuery}
        />

        {showTable && (
          <TableItems
            header={
              <>
                {headers.map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    key={item?.key}
                    order={item?.order}
                    markOderItem={itemOrder === item?.key}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: GetAllUsersFields, index) => (
              <TableRow key={`${item.user_uuid}-${item.client?.client_uuid}`}>
                <TableCell
                  maxLength={10}
                  ellipsis
                  centerTitle
                  className={classes.centerAccess}
                >
                  <IconButton
                    id={`access-users-list-${index}`}
                    onClick={() => {
                      handleClick(
                        item,
                        `/clients/${item?.client?.client_uuid}/users`
                      );
                    }}
                  >
                    <InputIcon className={classes.accessButton} />
                  </IconButton>
                </TableCell>
                <TableCell
                  className={classes.centerFields}
                  tooltip={`${item?.first_name} ${item?.last_name}`}
                >
                  {`${item?.first_name} ${item?.last_name}`}
                </TableCell>
                <TableCell
                  className={classes.emailField}
                  ellipsis
                  tooltip={item?.email}
                >
                  {item?.email}
                </TableCell>
                <TableCell
                  className={classes.centerFields}
                  tooltip={item?.client?.client_name}
                  ellipsis
                >
                  {item?.client?.client_name}
                </TableCell>
                <TableCell
                  className={classes.centerLongFields}
                  tooltip={item?.environment?.environment_name}
                  ellipsis
                >
                  {item?.environment?.environment_name}
                </TableCell>
                <TableCell
                  className={classes.centerLongFields}
                  tooltip={item?.partner?.partner_name}
                  ellipsis
                >
                  {item?.partner?.partner_name}
                </TableCell>
                <TableCell
                  className={classes.centerSmallFields}
                  tooltip={
                    item?.is_removing
                      ? i18n.t('REMOVING_KEY')
                      : i18n.t(item?.active ? 'ACTIVE_KEY' : 'INACTIVE_KEY')
                  }
                >
                  <Grid container justify="center">
                    {item?.is_removing ? (
                      <StatusIcon status="loading" />
                    ) : (
                      <StatusIcon
                        status={item?.active ? 'success' : 'inactive'}
                      />
                    )}
                  </Grid>
                </TableCell>
                <TableCell>
                  <Grid container justify="flex-start" spacing={1}>
                    <Grid item>
                      <Tooltip
                        arrow
                        title={
                          item?.mfa_configured
                            ? i18n.t('USER_MFA_ACTIVE_TOOLTIP')
                            : i18n.t('USER_MFA_INACTIVE_TOOLTIP')
                        }
                      >
                        {item?.mfa_configured ? (
                          <img
                            src="/images/mfaEnable.svg"
                            alt="Logo"
                            height={20}
                          />
                        ) : (
                          <img
                            src="/images/mfaDisable.svg"
                            alt="Logo"
                            height={20}
                          />
                        )}
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip
                        arrow
                        title={
                          item?.salt_configured
                            ? i18n.t('ACTIVE_SALT_KEY')
                            : i18n.t('INACTIVE_SALT_KEY')
                        }
                      >
                        {item?.salt_configured ? (
                          <img
                            src="/images/saltEnable.svg"
                            alt="Logo"
                            height={20}
                          />
                        ) : (
                          <img
                            src="/images/saltDisable.svg"
                            alt="Logo"
                            height={20}
                          />
                        )}
                      </Tooltip>
                    </Grid>

                    <Grid item>
                      {item?.environment.device_validation_enabled && (
                        <Tooltip arrow title={i18n.t('DEVICE_VALIDATION_KEY')}>
                          <img
                            src="/images/safeDeviceEnable.svg"
                            alt="Logo"
                            height={20}
                          />
                        </Tooltip>
                      )}
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell centerTitle className={classes.centerFields}>
                  {formatDateValue(item?.last_access)}
                </TableCell>
                <TableCell textAlignCenter>
                  <MenuActions id={`btn-options-users-${index}`}>
                    <Permission
                      value={item?.active ? 'inactivate_user' : 'activate_user'}
                    >
                      <MenuActionItem
                        id="btn-state-user"
                        onClick={() =>
                          item?.active
                            ? modalOpen({
                                title: i18n.t('WANT_DISABLE_USER_KEY'),
                                content: i18n.t('DISABLE_USER_DESCRIPTION_KEY'),
                                footer: (
                                  <Grid>
                                    <Button
                                      submitButton
                                      id="submit-button"
                                      onClick={() => {
                                        disableUserInClientRequest({
                                          userUuid: item?.user_uuid,
                                          id: item?.client.client_uuid,
                                          query,
                                          isListAllUsers: true,
                                        });
                                        modalClose();
                                        setRefresh(false);
                                      }}
                                    >
                                      {i18n.t('DISABLE_KEY')}
                                    </Button>
                                    <Button
                                      id="cancel-button"
                                      variant="outlined"
                                      onClick={modalClose}
                                    >
                                      {i18n.t('CANCEL_KEY')}
                                    </Button>
                                  </Grid>
                                ),
                              })
                            : modalOpen({
                                title: i18n.t('WANT_ACTIVATE_USERS_KEY'),
                                content: i18n.t('ACTIVE_USER_KEY', {
                                  id: `${item?.first_name} ${item?.last_name}`,
                                }),
                                footer: (
                                  <Grid>
                                    <Button
                                      submitButton
                                      id="submit-button"
                                      onClick={() => {
                                        activeUserInClientRequest({
                                          userUuid: item?.user_uuid,
                                          id: item?.client.client_uuid,
                                          query,
                                          isListAllUsers: true,
                                        });
                                        modalClose();
                                        setRefresh(false);
                                      }}
                                    >
                                      {i18n.t('ACTIVATE_KEY')}
                                    </Button>
                                    <Button
                                      id="cancel-button"
                                      variant="outlined"
                                      onClick={modalClose}
                                    >
                                      {i18n.t('CANCEL_KEY')}
                                    </Button>
                                  </Grid>
                                ),
                              })
                        }
                      >
                        {item?.active
                          ? i18n.t('DISABLE_KEY')
                          : i18n.t('ACTIVATE_KEY')}
                      </MenuActionItem>
                    </Permission>

                    <MenuActionItem
                      id="btn-edit-user"
                      onClick={() =>
                        modalOpen({
                          title: i18n.t('EDIT_USER_KEY'),
                          content: (
                            <FormUser
                              defaultValues={item}
                              onSubmit={values => {
                                editClientUserRequest({
                                  clientUuid: item?.client?.client_uuid,
                                  userUuid: item?.user_uuid,
                                  values: mapUserSubmit(values),
                                  isListAllUsers: true,
                                  query,
                                });
                                modalClose();
                              }}
                              onCancel={modalClose}
                            />
                          ),
                          maxWidth: 'sm',
                        })
                      }
                    >
                      {i18n.t('EDIT_KEY')}
                    </MenuActionItem>
                    <Permission value="list_environment_instance_sessions">
                      <MenuActionItem
                        id="btn-sessions"
                        onClick={() =>
                          modalOpen({
                            title: i18n.t('SESSIONS_KEY'),
                            content: <UserSession user={item} />,
                            genericFooter: { action: modalClose },
                            maxWidth: 'md',
                          })
                        }
                      >
                        {i18n.t('SESSIONS_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="delete_user">
                      <MenuActionItem
                        id="btn-remove-user"
                        onClick={() =>
                          modalOpen({
                            title: i18n.t('WANT_REMOVE_USER_KEY', {
                              name: `${item?.first_name} ${item?.last_name}`,
                            }),
                            content: i18n.t('REMOVE_USER_DESCRIPTION_KEY'),
                            footer: (
                              <Grid>
                                <Button
                                  submitButton
                                  id="submit-button"
                                  onClick={() => {
                                    removeClientUserRequest({
                                      clientUuid: item?.client.client_uuid,
                                      userUuid: item?.user_uuid,
                                      isListAllUsers: true,
                                      query,
                                    });
                                    modalClose();
                                  }}
                                >
                                  {i18n.t('REMOVE_KEY')}
                                </Button>
                                <Button
                                  id="cancel-button"
                                  variant="outlined"
                                  onClick={modalClose}
                                >
                                  {i18n.t('CANCEL_KEY')}
                                </Button>
                              </Grid>
                            ),
                          })
                        }
                      >
                        {i18n.t('USERS_PAGE.DELETE_BUTTON_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="delete_user">
                      <MenuActionItem
                        id="btn-remove-all-users"
                        onClick={() =>
                          modalOpen({
                            title: i18n.t(
                              'USERS_PAGE.DELETE_ALL_MODAL_TITLE_KEY'
                            ),
                            content: i18n.t(
                              'USERS_PAGE.DELETE_ALL_MODAL_CONTENT_KEY'
                            ),
                            footer: (
                              <Grid>
                                <Button
                                  submitButton
                                  id="submit-button"
                                  onClick={() => {
                                    removeUserFromAllClientsRequest({
                                      userUuid: item?.user_uuid,
                                      getUsersFn: () => getUsers(),
                                    });
                                    modalClose();
                                  }}
                                >
                                  {i18n.t('CONFIRM_KEY')}
                                </Button>
                                <Button
                                  id="cancel-button"
                                  variant="outlined"
                                  onClick={modalClose}
                                >
                                  {i18n.t('CANCEL_KEY')}
                                </Button>
                              </Grid>
                            ),
                          })
                        }
                      >
                        {i18n.t('USERS_PAGE.DELETE_ALL_BUTTON_KEY')}
                      </MenuActionItem>
                    </Permission>
                  </MenuActions>
                </TableCell>
              </TableRow>
            )}
            data={allUsersList}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('USERS_PAGE.NO_USERS_KEY')}
          />
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  users: { allUsersList, isLoading, isLoadingAllUsersList },
}: ApplicationState) => ({
  isLoading: isLoadingAllUsersList || isLoading,
  allUsersList,
  companySelected,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getAllUsersRequest,
  modalOpen,
  modalClose,
  removeClientUserRequest,
  disableUserInClientRequest,
  activeUserInClientRequest,
  editClientUserRequest,
  removeUserFromAllClientsRequest,
  selectCompany,
  selectPartner,
  exportUserSummaryRequest,
})(ListAllUsers);
